import React, { useCallback, useEffect, useState } from 'react';
import { GenericStep } from '@/components/FullWidthTemplate/GenericStep';
import * as Styles from './FilesMigration.styles';
import {
  IconButton, IconDownload, IconAttachedFile, IconLoading, ProgressBar, IconSql,
  Tag,
} from 'gatorcomponents';
import { isBRServer } from '@/utils/Validators/validation';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { ATIVACAO_MIGRATE_WP_FILE_STORAGE_SIZE } from '@/config/GrowthBook/constants';
import { useDispatch } from 'react-redux';
import { commonActions } from '@/redux/modules';

const FilesMigration = ({
  buttonsProps,
  locales,
  loading,
  setSelectedFiles,
}) => {
  const dispatch = useDispatch();
  const [file, setFile] = useState([]);
  const [usedSpace, setUsedSpace] = useState(0);
  const [usedSpacePercentage, setUsedSpacePercentage] = useState(0);
  const defaultSize = useFeatureValue(ATIVACAO_MIGRATE_WP_FILE_STORAGE_SIZE);
  const totalSpace = (defaultSize && defaultSize.size) || 1;
  const gbSize = 1024 * 1024 * 1024;


  useEffect(() => {
    setUsedSpacePercentage(((usedSpace / gbSize) / totalSpace) * 100);
  }, [usedSpace, totalSpace, gbSize]);

  const stepProps = useCallback(() => ({
    title: locales.title,
    buttonsProps: {
      ...buttonsProps,
      rightButton: {
        ...buttonsProps.rightButton,
        onClick: () => { buttonsProps.rightButton.onClick({ files: file }); },
      },
    },
  }), [locales, buttonsProps, file]);

  const formatFileSize = (size) => {
    if (size >= gbSize) {
      return `${(size / (gbSize)).toFixed(2)}GB`;
    }
    if (size >= 1024 * 1024) {
      return `${(size / (1024 * 1024)).toFixed(0)}MB`;
    }
    if (size >= 1024) {
      return `${(size / 1024).toFixed(0)}KB`;
    }
    return `${size}B`;
  };

  const handleFile = useCallback(({ files }) => {
    const newFiles = [...file];
    const { type } = files[0];

    const addFile = () => {
      newFiles.push(files[0]);
      const fileSize = files[0].size;

      if ((fileSize + usedSpace) <= (totalSpace * gbSize)) {
        setFile(newFiles);
        setSelectedFiles(newFiles);
        setUsedSpace(prevUsedSpace => prevUsedSpace + fileSize);
      }

      if ((fileSize + usedSpace) > (totalSpace * gbSize)) {
        dispatch(commonActions.notifications.set({
          toast: {
            showIcon: true,
            title: locales.fileSizeError.title,
            info: locales.fileSizeError.description(totalSpace),
            context: 'error',
          },
          maxWidth: '460px',
        }));
      }
    };

    if (file.length === 0) {
      addFile();
    }

    if (file.length === 1 && type !== file[0].type) {
      addFile();
    }
  }, [
    file,
    setFile,
    setSelectedFiles,
    totalSpace,
    gbSize,
    dispatch,
    locales,
    usedSpace,
  ]);

  const handleChangeFiles = (event) => {
    const { target } = event;
    const { files } = target;
    event.preventDefault();
    handleFile({ files });
    target.value = null;
  };

  const handleFileDrop = (event) => {
    if (file.length < 2) {
      const { dataTransfer, target } = event;
      const { files } = dataTransfer;
      event.preventDefault();
      target.value = null;
      handleFile({ files });
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleRemove = (index) => {
    const removedFileSize = file[index].size;
    setFile(prevFiles => prevFiles.filter((_, i) => i !== index));
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    setUsedSpace(prevUsedSpace => prevUsedSpace - removedFileSize);
  };

  const disabledInput = file.length > 1;

  return (
    <GenericStep {...stepProps()}>
      <Styles.DescriptionWrapper>
        {locales.description}
      </Styles.DescriptionWrapper>

      <Styles.Doubt>
        {locales.doubt}
      </Styles.Doubt>

      <Styles.ProgressBarLine>
        <Styles.ProgressBarLabel>{locales.storeSpace}</Styles.ProgressBarLabel>
        <Styles.ProgressBarWrapper>
          <ProgressBar progress={usedSpacePercentage} />
        </Styles.ProgressBarWrapper>
        <Styles.ProgressBarLabel>
          {`${formatFileSize(usedSpace)} ${isBRServer ? 'de' : 'a'} ${totalSpace}GB`}
        </Styles.ProgressBarLabel>
      </Styles.ProgressBarLine>

      <Styles.DragAndDropArea
        onDrop={handleFileDrop}
        onDragOver={handleDragOver}
        htmlFor="file-upload"
        $disabled={disabledInput}
        data-testid="drag-and-drop-text"
      >
        <IconDownload color={disabledInput ? 'lowMedium' : 'primaryMedium'} />
        <Styles.InvisibleInput
          type="file"
          id="file-upload"
          data-testid="file-upload"
          onChange={handleChangeFiles}
          disabled={disabledInput}
          accept=".zip,.sql"
        />
        {locales.dragText}
      </Styles.DragAndDropArea>

      <Styles.AttachedFiles>
        {file.map((fileItem, index) => (
          <Styles.AttachedFile key={fileItem.name}>
            {fileItem.name.includes('.zip') && (
              <IconAttachedFile color="lowPure" />
            )}

            {fileItem.name.includes('.sql') && (
              <IconSql color="lowPure" />
            )}

            <Styles.FileName>
              {fileItem.name}
            </Styles.FileName>

            {fileItem.size && formatFileSize(fileItem.size)}

            {loading && (
              <Tag variant="pending" size="sm" label={locales.processLabel} />
            )}

            <Styles.ActionWrapper>
              {loading
                ? <IconLoading />
                : <IconButton variant="white" icon="IconClose" onClick={() => handleRemove(index)} />
              }
            </Styles.ActionWrapper>
          </Styles.AttachedFile>
        ))}
      </Styles.AttachedFiles>
    </GenericStep>
  );
};

export default FilesMigration;
